import React from 'react'

import {
    makeStyles,
    Theme,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Radio,
    Button,
} from '@material-ui/core'

import { PeriodOption } from '../../models/Frequency/types'
import { InspectionType } from '../../models/Config/types'
import { Unit } from '../../models/Infrastructure/types'
import { Selector } from '../../components/Selector'
import {
    FacilityItem,
    FacilitiesState,
} from '../../hooks/useFacilitiesPlan/types'
import { PERIOD_LABELS } from '../../hooks/useFacilitiesPlan/useFacilitiesPlan'

interface PeriodOptionItem {
    id: number
    name: string
    value: PeriodOption
}

const PERIOD_OPTIONS: PeriodOptionItem[] = Object.entries(PeriodOption).map(
    ([key, value], index) => ({
        id: index,
        name: PERIOD_LABELS[value],
        value,
    }),
)

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 400px)',
        overflowX: 'auto',
        flex: 1,
    },
    headerCell: {
        backgroundColor: theme.palette.grey[100],
        fontWeight: 'bold',
        padding: theme.spacing(1.5),
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    radioGroup: {
        flexDirection: 'row',
        justifyContent: 'center',
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    radio: {
        padding: 6,
    },
    addButton: {
        padding: 4,
    },
    facilityNameCell: {
        width: '15%',
        minWidth: 150,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    radioCell: {
        width: '8%',
        minWidth: 80,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    unitCell: {
        width: '25%',
        minWidth: 250,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    frequencyCell: {
        width: '15%',
        minWidth: 150,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    inspectionTypeCell: {
        width: '25%',
        minWidth: 250,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    submitButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
}))

interface SimpleAreaFacilitiesProps {
    availableInspectionTypes?: InspectionType[]
    inspectionUnits?: Unit[]
    facilities: FacilityItem[]
    facilitiesState: FacilitiesState
    onChange?: (state: {
        facilities: FacilitiesState
        inspectionTypes: InspectionType[]
    }) => void
    onFacilityChange: (id: string, value: 'yes' | 'no' | null) => void
    onPeriodChange: (id: string, period: PeriodOption) => void
    onUnitChange: (id: string, unit: Unit | null) => void
    onInspectionTypeChange: (id: string, type: InspectionType | null) => void
    onSubmit: () => void
}

export const SimpleAreaFacilities: React.FC<SimpleAreaFacilitiesProps> = ({
    onChange,
    availableInspectionTypes = [],
    inspectionUnits = [],
    facilities,
    facilitiesState,
    onFacilityChange,
    onPeriodChange,
    onUnitChange,
    onInspectionTypeChange,
    onSubmit,
}) => {
    const classes = useStyles()

    const handleChange = (id: string, value: 'yes' | 'no' | null) => {
        // First update the local facility state
        const newFacilitiesState = {
            ...facilitiesState,
            [id]: {
                ...facilitiesState[id],
                value,
                // Clear the unit selection if setting to 'no'
                selectedUnit:
                    value === 'no' ? null : facilitiesState[id]?.selectedUnit,
            },
        }

        // Then notify parent components
        onFacilityChange(id, value)
        onChange?.({
            facilities: newFacilitiesState,
            inspectionTypes: availableInspectionTypes,
        })
    }

    const handleFrequencyChange = (id: string, period: PeriodOption) => {
        // Update local state first
        const newFacilitiesState = {
            ...facilitiesState,
            [id]: {
                ...facilitiesState[id],
                period,
            },
        }

        // Then notify parent components
        onPeriodChange(id, period)
        onChange?.({
            facilities: newFacilitiesState,
            inspectionTypes: availableInspectionTypes,
        })
    }

    const getUnitError = (id: string) => {
        const facility = facilitiesState[id]
        if (!facility) return undefined
        if (facility.value === 'no') return undefined
        if (facility.value === null) return undefined
        if (!facility.selectedUnit) return true
        return undefined
    }

    return (
        <Paper className={classes.root}>
            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={`${classes.headerCell} ${classes.facilityNameCell}`}
                            >
                                Select if your property has these items
                            </TableCell>
                            <TableCell
                                align="center"
                                className={`${classes.headerCell} ${classes.radioCell}`}
                            >
                                Yes
                            </TableCell>
                            <TableCell
                                align="center"
                                className={`${classes.headerCell} ${classes.radioCell}`}
                            >
                                No
                            </TableCell>
                            <TableCell
                                align="center"
                                className={`${classes.headerCell} ${classes.unitCell}`}
                            >
                                Unit
                            </TableCell>
                            <TableCell
                                align="center"
                                className={`${classes.headerCell} ${classes.frequencyCell}`}
                            >
                                Frequency
                            </TableCell>
                            <TableCell
                                align="center"
                                className={`${classes.headerCell} ${classes.inspectionTypeCell}`}
                            >
                                Inspection Type
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {facilities.map((facility) => (
                            <TableRow
                                key={facility.id}
                                className={classes.tableRow}
                            >
                                <TableCell className={classes.facilityNameCell}>
                                    {facility.name}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.radioCell}
                                >
                                    <Radio
                                        checked={facility.value === 'yes'}
                                        onChange={() =>
                                            handleChange(facility.id, 'yes')
                                        }
                                        value="yes"
                                        size="small"
                                        className={classes.radio}
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.radioCell}
                                >
                                    <Radio
                                        checked={facility.value === 'no'}
                                        onChange={() =>
                                            handleChange(facility.id, 'no')
                                        }
                                        value="no"
                                        size="small"
                                        className={classes.radio}
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.unitCell}
                                >
                                    <Selector
                                        customStyle={{
                                            select: {
                                                width: '100%',
                                                minWidth: 250,
                                                textAlign: 'left',
                                            },
                                            menuItem: {
                                                justifyContent: 'flex-start',
                                            },
                                        }}
                                        currentValue={
                                            facility.selectedUnit?.id || -1
                                        }
                                        onChange={(e) => {
                                            const selectedUnit =
                                                inspectionUnits.find(
                                                    (u) =>
                                                        u.id === e.target.value,
                                                ) || null
                                            onUnitChange(
                                                facility.id,
                                                selectedUnit,
                                            )
                                        }}
                                        data={inspectionUnits}
                                        getDisplayString={(unit) => unit.name}
                                        disabled={facility.value !== 'yes'}
                                        variant="outlined"
                                        size="small"
                                        searchable
                                        noSelectionLabel="Select Unit"
                                        error={getUnitError(facility.id)}
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.frequencyCell}
                                >
                                    <Selector
                                        customStyle={{
                                            select: {
                                                width: '100%',
                                                minWidth: 150,
                                                textAlign: 'left',
                                            },
                                            menuItem: {
                                                justifyContent: 'flex-start',
                                            },
                                        }}
                                        currentValue={
                                            PERIOD_OPTIONS.find(
                                                (p) =>
                                                    p.value === facility.period,
                                            )?.id || 0
                                        }
                                        onChange={(e) => {
                                            const option = PERIOD_OPTIONS.find(
                                                (p) => p.id === e.target.value,
                                            )
                                            if (option) {
                                                handleFrequencyChange(
                                                    facility.id,
                                                    option.value,
                                                )
                                            }
                                        }}
                                        data={PERIOD_OPTIONS}
                                        getDisplayString={(item) => item.name}
                                        disabled={facility.value !== 'yes'}
                                        variant="outlined"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={classes.inspectionTypeCell}
                                >
                                    <Selector
                                        customStyle={{
                                            select: {
                                                width: '100%',
                                                minWidth: 250,
                                                textAlign: 'left',
                                            },
                                            menuItem: {
                                                justifyContent: 'flex-start',
                                            },
                                        }}
                                        currentValue={
                                            facility.inspectionType?.id || -1
                                        }
                                        onChange={(e) => {
                                            const selectedType =
                                                availableInspectionTypes.find(
                                                    (t) =>
                                                        t.id === e.target.value,
                                                ) || null
                                            onInspectionTypeChange(
                                                facility.id,
                                                selectedType,
                                            )
                                        }}
                                        data={availableInspectionTypes}
                                        getDisplayString={(type) => type.name}
                                        disabled={facility.value !== 'yes'}
                                        variant="outlined"
                                        size="small"
                                        searchable
                                        noSelectionLabel="Select Type"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={classes.submitButton}>
                <Button variant="contained" color="primary" onClick={onSubmit}>
                    Create Facilities Plan
                </Button>
            </div>
        </Paper>
    )
}
